import { useQuery } from '@tanstack/react-query';

import { DESKTOP_TARGETS_QUERY_KEY } from '@heidi/agents/contracts';
import { desktopTargetsClient, type DesktopTarget } from './desktopTargets';

const QUEUE_POLL_INTERVAL_MS = 5_000;

/**
 * While a computer-use task is running but has produced no steps yet, poll
 * the desktop-target roster to detect the queued state: EVERY target's
 * single assignment slot is held by a DIFFERENT run, meaning this task is
 * waiting at the resource gate and will start automatically when a holder
 * finishes. Returns the busy target (for its display name) or null.
 *
 * The all-targets-busy requirement is deliberate: the roster does not tell
 * us which target this turn selected, so a single unrelated busy machine
 * must not make a run whose own target is free look queued. When any
 * target is free we report not-queued.
 *
 * ``childAgentRunId`` is required for a positive signal — without it we
 * can't distinguish "queued behind another run" from "our own run booting
 * while holding the slot", so we report not-queued.
 */
export const useComputerUseQueuedTarget = ({
  childAgentRunId,
  enabled,
}: {
  childAgentRunId: string | undefined;
  enabled: boolean;
}): DesktopTarget | null => {
  const active = enabled && Boolean(childAgentRunId);
  const query = useQuery({
    queryKey: DESKTOP_TARGETS_QUERY_KEY,
    queryFn: async () => (await desktopTargetsClient.list()).data,
    enabled: active,
    refetchInterval: active ? QUEUE_POLL_INTERVAL_MS : false,
  });
  if (!active) return null;
  const targets = query.data?.targets;
  if (!targets || targets.length === 0) return null;
  const busyWithOtherRun = targets.filter(
    (target) => target.busy_agent_run_id && target.busy_agent_run_id !== childAgentRunId
  );
  // Queued only when no target could serve this run: if any slot is free (or
  // held by this run itself), the run is booting rather than queued.
  if (busyWithOtherRun.length !== targets.length) return null;
  return busyWithOtherRun[0] ?? null;
};
